import {
  AttributeType,
  EqlAttributeConfigurationDc,
  GeometryType,
  LayerReferenceConfigurationDc,
  StringSubType,
} from "@evergis/api";
import { Icon, IconToggle, IconTypesKeys } from "@evergis/ui";
import React, { FC, useCallback } from "react";
import { DraggableProvided } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import { getFieldTypeIcon } from "../../../../../evergis/core/attributes/fieldTypes";
import { AttributeFormat } from "../../../DialogComponents/Attributes/AttributeFormat/AttributeFormat";
import { AttributeActions } from "./AttributeActions";
import { AttributeAlias } from "./AttributeAlias";
import { QueryAttributeActions, QueryAttributeContainer, QueryAttributeMeta, QueryAttributeType } from "./styled";
import { EqlAttribute } from "./types";

export interface QueryLayerAttributeProps {
  attribute: EqlAttribute;
  draggableProvided?: DraggableProvided;
  updateAttribute: (name: string, attributeUpdate: Partial<EqlAttributeConfigurationDc>) => void;
  deleteAttribute: (name: string) => void;
  deleteSysAttribute: (name: string) => void;
  displayField: string;
  setDisplayField: (fieldName: string) => void;
  layerReferences?: LayerReferenceConfigurationDc[];
  geometryType?: GeometryType;
  canDelete?: boolean;
}

// eslint-disable-next-line max-lines-per-function
export const QueryLayerAttribute: FC<QueryLayerAttributeProps> = ({
  attribute,
  deleteAttribute,
  deleteSysAttribute,
  updateAttribute,
  draggableProvided,
  displayField,
  setDisplayField,
  layerReferences,
  geometryType,
  canDelete,
}) => {
  const { t } = useTranslation();
  const { attributeName, isDisplayed, type, alias, subType, stringFormat, expression, description, sysAttribute } =
    attribute;

  const toggleDisplayed = useCallback(() => {
    updateAttribute(attributeName, {
      isDisplayed: !isDisplayed,
    });
  }, [attributeName, isDisplayed, updateAttribute]);

  const updateAlias = useCallback(
    text => {
      updateAttribute(attributeName, {
        alias: text,
      });
    },
    [attributeName, updateAttribute],
  );

  const typeIcon = getFieldTypeIcon(type, subType) as IconTypesKeys;
  const canBeDisplayField =
    type === AttributeType.String && (!subType || subType === StringSubType.None) && !sysAttribute;

  const updateType = useCallback(
    (newType: AttributeType | StringSubType) => {
      let typeFormatted;

      if (newType === StringSubType.Image) {
        typeFormatted = {
          type: AttributeType.String,
          subType: StringSubType.Image,
        };
      } else if (newType === StringSubType.Attachments) {
        typeFormatted = {
          type: AttributeType.Json,
          subType: StringSubType.Attachments,
        };
      } else {
        typeFormatted = {
          type: newType,
        };
      }

      updateAttribute(attributeName, typeFormatted);
    },
    [attributeName, updateAttribute],
  );

  return (
    <QueryAttributeContainer
      {...draggableProvided.draggableProps}
      {...draggableProvided.dragHandleProps}
      ref={draggableProvided.innerRef}
    >
      <IconToggle
        onClick={toggleDisplayed}
        isSelected={isDisplayed}
        kind={isDisplayed ? "password_show" : "password_hide"}
        title={
          isDisplayed
            ? t("editResource.attributeOff", { ns: "dialogs" })
            : t("editResource.attributeOn", { ns: "dialogs" })
        }
      />
      <QueryAttributeMeta>
        <AttributeAlias
          isDisplayField={displayField === attributeName}
          alias={alias || attributeName}
          updateAlias={updateAlias}
          type={type}
          subType={subType}
          updateType={updateType}
        />
        <QueryAttributeType>
          {typeIcon && <Icon kind={typeIcon} />}
          <div>{attributeName}</div>
        </QueryAttributeType>
      </QueryAttributeMeta>
      <QueryAttributeActions>
        <AttributeFormat
          type={type}
          subType={subType}
          geometryType={geometryType}
          id={attributeName}
          updateAttribute={updateAttribute}
          stringFormat={stringFormat}
          isCalculated={!!expression}
          description={description}
          attributeConfiguration={attribute}
          layerReferences={layerReferences}
          withoutInput
        />
        {(canBeDisplayField || canDelete) && (
          <AttributeActions
            canDelete={canDelete}
            canBeDisplayField={canBeDisplayField}
            isDisplayField={displayField === attributeName}
            toggleDisplayField={() => setDisplayField(displayField === attributeName ? void 0 : attributeName)}
            deleteAttribute={() => (sysAttribute ? deleteSysAttribute(attributeName) : deleteAttribute(attributeName))}
          />
        )}
        {!(canBeDisplayField || canDelete) && <span style={{ width: "2rem" }} />}
      </QueryAttributeActions>
    </QueryAttributeContainer>
  );
};
